/**
 * THE STICKY-TABLE-HEADER TREATMENT — one rule, three surfaces.
 *
 * Used by `components/OrderTableView.js` (`OrderTableShell`),
 * `components/gags/GagGroupTable.js` and, since 2026-09-26,
 * `components/budget/CrewMembersTableFrame.js`. Extracted 2026-08-08 when the Breakdown
 * needed what the order pages had just been given: per the Code Unification
 * Principle, the SECOND use is where a thing becomes shared, not the third.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🚨 THE RULE, WHICH IS THE ACTUAL SHARED ASSET — the class strings below are
 * just its consequences.
 * ══════════════════════════════════════════════════════════════════════
 *
 * **A STICKY TABLE HEADER CANNOT PIN TO THE PAGE FROM INSIDE A HORIZONTALLY
 * SCROLLABLE CONTAINER. THE TWO ARE MUTUALLY EXCLUSIVE ON THE SAME ELEMENT.**
 *
 * `position: sticky` resolves against the nearest SCROLL CONTAINER, and an
 * element is a scroll container if the computed value of overflow-x **or**
 * overflow-y is `auto` or `scroll`. A data table wide enough to need
 * `overflow-x: auto` is therefore already a scroll container, and any sticky
 * `<th>` inside it resolves against THAT box — never against the page.
 *
 * 🪤 **`overflow-y: clip` DOES NOT RESCUE IT.** This was tried on the order
 * pages and shipped a visible defect. The theory was that `clip` creates no
 * scroll container, so the headers would fall through to the page scroller.
 * False: the element is *already* a scroll container by virtue of its x-axis,
 * and clipping the y-axis does not un-make it. The result was worse than inert
 * — the box never scrolls vertically, so a `top: <offset>` was applied as a
 * plain offset from the container's own top edge and **parked every heading
 * ~100px down, on top of the first rows, at rest.** Product owner: *"table
 * headder is now on top of the first item in the list"*. Do not re-attempt it.
 *
 * ✅ **THE FIX IS TO CHANGE THE SCROLL MODEL, NEVER TO RETUNE `sticky`** — and
 * since 2026-09-26 every table here uses ONE model, 🅱️. (This block called the
 * inner region "the only" fix until that morning, which was false.)
 *
 * 🅰️ **RETIRED — THE INNER REGION (2026-08-08 → 2026-09-26).** One element
 * owned BOTH axes and was height-capped at `calc(100vh - <its measured
 * document top>)`, so the table scrolled vertically *inside itself* and the
 * `<th>`s pinned at `top: 0` of it. It worked, and its cost — a scroll window
 * inside a scrolling page — was accepted for a while. **The owner has ruled
 * that cost out**: *"a scroll within a scroll? not nice UI desing"* →
 * *"One page scroll, pinned header"* (the order tables), then *"yh this worked
 * well do all other similar pages"* (the Breakdown and the Budget crew table,
 * same day). Its helpers — `STICKY_REGION_AXES_MD`, `stickyRegionMaxHeight`,
 * `resolveStickyRegionTop` and `hooks/useMeasuredDocumentTop` — lost their last
 * caller and were DELETED (pre-deletion SHA `3e60c14d`). Do not rebuild it.
 *
 * 🅱️ **THE SPLIT HEADER — every table on the page scroll.** Take the header
 * OUT of the horizontal scroller: a header-only `<table>` in its own pane, that
 * pane (or a sticky block containing it) `sticky` against the PAGE, the rows in
 * an x-only pane with no height cap (`PAGE_SCROLL_PANE_X`), a sticky-bottom bar
 * while the table overflows (`components/ui/StickyScrollbar`, the 2026-08-10
 * ruling), and every pane's `scrollLeft` linked two-way by
 * `hooks/useLinkedScrollLeft`. Whatever wraps them must NOT be a scroll
 * container (`overflow: clip` on BOTH axes is not one — the 🪤 above is about
 * `clip` on an element whose OTHER axis already scrolls). ARIA roles re-join
 * the two `<table>`s into one for a screen reader. Alignment needs ONE width
 * source for both tables. Three users, three shapes of the same model:
 *   - `OrderTableShell` — one sticky header pane at `stickyTop` (declared
 *     `<colgroup>` widths);
 *   - `GagGroupTable` — per GROUP, a sticky HEAD (title + header pane) at
 *     `groupHeaderTop`, contained by its card so the next group's head takes
 *     over; one bar for every group (declared `<colgroup>` widths);
 *   - the Budget crew table (`components/budget/CrewMembersTableFrame`) — its
 *     column widths are CONTENT-sized, so they are MEASURED from the rows table
 *     and applied to the header and TOTAL panes; the total row is a
 *     sticky-bottom footer pane above the bar.
 *
 * ⚠️ **NOTHING UNDER `client/src` CAN PROVE ANY OF THIS.** jsdom applies no
 * stylesheet, has no layout engine and no viewport. The tests assert the class
 * strings, the inline `maxHeight` and the offsets. **Whether a header actually
 * pins, whether the page gains a second scrollbar, and whether a short list
 * stays short are BROWSER checks.**
 */

/**
 * 🅱️'s pane: a HORIZONTAL-ONLY scroller whose own scrollbar is hidden. Added
 * 2026-09-26 for `OrderTableShell`'s header and rows panes.
 *
 * 🔑 **`overflow-y-hidden`, stated, never left to default.** `overflow-x: auto`
 * forces a `visible` y-axis to COMPUTE as `auto`, so an x-scroller is always a
 * y-scroll container on paper; `hidden` with no height cap clips nothing and
 * makes "no vertical scroller around the rows" true of the computed style. 🚨
 * Never pair it with a `max-height` — that is 🅰️ come back.
 *
 * 🔑 The bar is hidden because the page carries ONE sticky-bottom scrollbar
 * instead; a pane using this class without that bar strands mouse users.
 * Literal arbitrary properties, never assembled (the Tailwind rule above).
 */
export const PAGE_SCROLL_PANE_X = 'overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * 🚨 **THE REGION MUST BE A CONTAINING BLOCK, OR THE CAP ABOVE IS A LIE.**
 * Added 2026-08-11 after the product owner reported *"why is there so much
 * vertical scroll space UNDER the table?"* on Department Orders.
 *
 * ── The measurement, first ────────────────────────────────────────────
 * Department Orders, Table layout, 1280×900, 100 rows of which 82 actionable,
 * measured in Chromium against a copy of the dev database:
 *
 *     document.scrollHeight   6153      real content bottom   998
 *     page scroll range       5253      DEAD SPACE          5155px
 *
 * The region itself was **perfect**: `docTop` 161, `max-height` 739 (= 900−161),
 * bottom exactly on the fold. The 5,155px lived nowhere in the layout.
 *
 * ── The cause, which is a CSS containment rule and not a sizing bug ───
 * `OrderTableView` renders `<span className="sr-only">` on every ACTIONABLE
 * row. Tailwind's `sr-only` is `position: absolute`. Every ancestor from that
 * `<td>` up to `<html>` was `position: static`, so each span's containing block
 * was the **INITIAL containing block**.
 *
 * 🔑 **An ancestor's `overflow` clips only those descendants that are in its
 * CONTAINING-BLOCK chain.** An absolutely-positioned box that resolves against
 * the ICB is therefore NOT clipped by this region, however tightly the region
 * is capped — it lays out at its static position, thousands of pixels down
 * inside the table, and extends the *document's* scrollable overflow to reach
 * it. 82 spans, deepest at document y **6153**, which is `scrollHeight` to the
 * pixel. That identity is the proof; it is not a correlation.
 *
 * The user then scrolls 5,253px of empty page beneath a table that has already
 * ended — and the region, which is NOT sticky, scrolls up and out of the
 * viewport on the way, **taking its horizontal scrollbar below the fold** — the
 * defect the (since deleted) measured-top cap had just fixed, arriving by a
 * second route.
 *
 * ── Why the fix is `relative` and belongs HERE ────────────────────────
 * `position: relative` makes the region the containing block for every
 * absolutely-positioned descendant, so the clip it already performs finally
 * applies to them. Measured: document 6153 → **1039**, dead space 5155 → **0**,
 * with the table, the cap, the sticky headings and the row geometry all
 * byte-identical.
 *
 * 🔑 **It closes the CLASS, not the instance.** Removing that one `sr-only`
 * would fix today's symptom and leave the next absolutely-positioned child —
 * a badge, a tooltip, another `sr-only` — free to reopen it, invisibly, because
 * nothing about the markup looks wrong and jsdom cannot see it.
 *
 * ⚠️ **`z-index` stays `auto`, deliberately.** `position: relative` with
 * `z-index: auto` creates NO stacking context, so the Toolbar z-index contract
 * is untouched: the sticky `<th>`s still resolve against this same scroll
 * container, and nothing portalled to `document.body` changes tier.
 *
 * 🪤 **`contain: strict` also removes the phantom height and is WRONG** —
 * measured, it took the document to 900px by removing the region's contribution
 * to its parent's size altogether. Do not reach for it.
 *
 * 🔀 **SINCE 2026-09-26 EVERY PAGE-SCROLL PANE THAT CLIPS CARRIES IT** — the
 * order shell's wrapper and rows pane, every Breakdown header and rows pane and
 * its outer region, the crew table's panes. The rule outlived the cap it was
 * written against: each pane still CLIPS (`overflow-x: auto` + `overflow-y:
 * hidden`), and a clip reaches only descendants in its containing-block chain.
 * The Breakdown was clean "by accident of content" before (no `sr-only`,
 * relative `<th>`s, portalled popups — measured 0 escaping descendants); it now
 * carries the class by construction, pinned with the others by
 * `components/stickyTables.noCap.wiring.test.js` §2.
 *
 * ⚠️ **NOTHING IN jsdom CAN SEE ANY OF THIS.** There is no stylesheet, so
 * `sr-only` is not `position: absolute` there; no layout, so nothing overflows;
 * no viewport, so no document height. The tests assert that the class is
 * DECLARED on the region. **Whether the page has stopped scrolling is a BROWSER
 * check** — this one was performed in Chromium at 375 / 1024 / 1280 / 1440 in
 * both themes.
 */
export const STICKY_REGION_CONTAINMENT = 'relative';

/**
 * ══════════════════════════════════════════════════════════════════════
 * THE Z-INDEX TIERS — an extension of the Toolbar z-index contract.
 * ══════════════════════════════════════════════════════════════════════
 *
 * The app-wide contract (CLAUDE.md, `components/ui/Toolbar.js`) runs:
 *
 *     z-[1]  FROZEN (sticky-left) BODY cells      ← added 2026-08-09
 *     z-[5]  sticky COLUMN headers
 *     z-[6]  the FROZEN HEADER CORNER             ← added 2026-08-09
 *     z-10   sticky group/section headers
 *     z-20   the toolbar
 *     z-50   dropdown panels (opened inside the toolbar's stacking context)
 *     z-60   THE ESCAPE TIER — anything PORTALLED to document.body because it
 *            cannot win where it sits. Two users since 2026-08-09:
 *            GagGroupTable's kebab, and ui/Dropdown's opt-in `portal` prop
 *            (the Breakdown's inline Scenes picker, whose cell `truncate`s and
 *            so CLIPPED an anchored panel — a clip, not a layering problem;
 *            no z value could have fixed it). Inline `zIndex: 60` at both.
 *
 * 🔀 **MERGE 2026-08-09 — TWO branches each extended this contract and each
 * dropped the other's addition.** The frozen-column work added `z-[1]` and
 * `z-[6]`; the Scenes-picker un-clipping work rewrote `z-60` from "a kebab
 * menu" into the general ESCAPE TIER with its two users and the clip-vs-layer
 * distinction. **Both are kept.** Taking either side whole would have silently
 * deleted a tier that something is currently relying on — and a z-index
 * contract with a missing rung is worse than none, because the next person
 * reads it as complete.
 *
 * **Sticky COLUMN headers needed a tier that did not exist**, because on the
 * Breakdown they must paint UNDER the sticky group title and OVER the rows.
 * They take `z-[5]`, and the contract is amended in CLAUDE.md in the same
 * commit — a local number with no entry in the contract is exactly the guess
 * the contract exists to prevent.
 *
 * 🔑 **BELOW the group header, not above.** The group title says *which*
 * sub-department or shoot week you are looking at; the column headers say what
 * the columns are. When the two meet as a group scrolls away, the title must
 * win — it is the one that is about to be replaced by the next group's.
 */
export const STICKY_COLUMN_HEADER_Z = 'z-[5]';

/**
 * A FROZEN (sticky-left) BODY cell — the Breakdown's expand chevron and Title,
 * added 2026-08-09. Two neighbours decide this number and both are hard bounds:
 *
 * 🔑 **ABOVE the ordinary body cells** it slides over as the table scrolls
 * sideways. A `position: sticky` cell is a POSITIONED box, so it already paints
 * above its non-positioned siblings at `z-index: auto` — but that is an
 * accident of the painting algorithm rather than a stated intent, and it stops
 * being true the moment anything else in a row acquires a z-index. Stating the
 * tier costs nothing and removes the question.
 *
 * 🔑 **BELOW `STICKY_COLUMN_HEADER_Z` (`z-[5]`)**, which is the binding one:
 * these cells are pinned horizontally and the column headers are pinned
 * vertically, so they MEET in the top-left corner of the region on any diagonal
 * scroll. The header row must win there for exactly the reason the group title
 * wins over the header row — the heading names the column the cell is in.
 *
 * 🪤 It also makes the cell a STACKING CONTEXT (a sticky box with a z-index
 * other than `auto` always does), so nothing inside a frozen cell can be lifted
 * out of it. That is fine for what is in there today — a chevron button and a
 * text input — and it is why the Scenes picker's in-flow panel and the Actions
 * kebab's portal must never be moved into a frozen column. The same fact is
 * already recorded against the resize handle, whose `z-10` cannot escape its
 * own `z-[5]` `<th>`.
 */
export const STICKY_COLUMN_CELL_Z = 'z-[1]';

/**
 * The FROZEN HEADER CORNER — a `<th>` of a frozen column, which is sticky on
 * BOTH axes (`top` from the column-header offset, `left` from the frozen
 * column's own offset). Added 2026-08-09.
 *
 * 🚨 **IT NEEDS ITS OWN TIER ABOVE `z-[5]`, AND EQUAL WOULD NOT DO.** Its
 * horizontal neighbours are the other sticky `<th>`s at `z-[5]`, and equal
 * z-index resolves by DOM order — the frozen columns are FIRST, so the columns
 * scrolling under them would paint on top and the corner would disappear behind
 * its own row. One tier up is the whole fix.
 *
 * 🔑 **BELOW the group title's `z-10`, unchanged.** The corner and the group
 * title are both pinned to the region's left edge, they overlap in the 1px seam
 * the title's background deliberately covers, and the title must keep winning
 * there — that decision is the reason `z-[5]` exists and nothing about freezing
 * a column changes it.
 */
export const STICKY_COLUMN_CORNER_Z = 'z-[6]';

/** The existing tier the sticky group/section headers already use. */
export const STICKY_SECTION_HEADER_Z = 'z-10';

/**
 * A sticky header cell's non-background classes.
 *
 * 🚨 **THE BACKGROUND IS THE CALLER'S AND IS NOT OPTIONAL.** A `<thead>`'s own
 * fill does NOT travel with a sticky child: the `<th>` pins as a transparent
 * box and every row scrolls visibly through the headings. The two surfaces sit
 * on different fills — the order table's `<thead>` is `bg-gray-50
 * dark:bg-gray-700/50`, the Breakdown's header row inherits its card's
 * `bg-white dark:bg-gray-800` — so the background is passed in rather than
 * baked here, and each caller restates the fill its own section already has.
 * Getting this wrong is invisible until something scrolls.
 */
export const STICKY_HEADER_CELL_BASE = 'sticky';

/** The order table's `<thead>` fill, restated on its sticky header PANE (on
 * its sticky cells until 2026-09-26, when the pane became the sticky element). */
export const ORDER_TABLE_HEADER_BG = 'bg-gray-50 dark:bg-gray-700/50';

/** The Breakdown's group card fill, restated on its sticky header cells. */
export const GAG_TABLE_HEADER_BG = 'bg-white dark:bg-gray-800';
